const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5215';

// ─── Database Provisioning ───────────────────────────────────────────────────

export const provisionDatabase = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/provision-database`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Connection Strings ──────────────────────────────────────────────────────

export const getBackendTargets = async (tenantSlug) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/backend-targets/${tenantSlug}`);
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const pushConnectionStrings = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/push-connection-strings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Environment Variable Resolution ─────────────────────────────────────────

export const resolveEnv = async (tenantSlug) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/resolve-env/${tenantSlug}`);
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Code Generation ─────────────────────────────────────────────────────────

export const codeGenDryRun = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/code-gen/dry-run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const codeGenGenerate = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/code-gen/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Full Orchestration (SSE-based) ──────────────────────────────────────────

export const startOnboarding = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const resumeOnboarding = async (request) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const createOnboardingStream = (sessionId) => {
  return new EventSource(`${API_BASE_URL}/api/tenant-onboarding/stream/${sessionId}`);
};

export const cancelOnboarding = async (sessionId) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/cancel/${sessionId}`, { method: 'POST' });
  if (!response.ok) {
    const data = await safeJson(response);
    throw createError(response, data);
  }
};

export const getSession = async (sessionId) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/session/${sessionId}`);
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const getOnboardingHistory = async (count = 20) => {
  const response = await fetch(`${API_BASE_URL}/api/tenant-onboarding/history?count=${count}`);
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Infrastructure (existing provisioning endpoints) ────────────────────────

export const createUnifiedResourceGroup = async (tenantName) => {
  const response = await fetch(`${API_BASE_URL}/api/tenantprovisioning/unified/create-resource-group`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantName }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const validateUnifiedDeployment = async (tenantName) => {
  const response = await fetch(`${API_BASE_URL}/api/tenantprovisioning/unified/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantName }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

export const deployUnified = async (tenantName) => {
  const response = await fetch(`${API_BASE_URL}/api/tenantprovisioning/unified/deploy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantName }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw createError(response, data);
  return data;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function safeJson(response) {
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return { message: text }; }
}

function createError(response, data) {
  const err = new Error(data?.message || data?.error || `HTTP error! status: ${response.status}`);
  err.status = response.status;
  err.details = data;
  return err;
}
